import { FormEvent, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import REFERRAL_INFO from '@/graphql/queries/referral-info.graphql';
import SET_SPONSOR from '@/graphql/mutations/set-sponsor.graphql';
import Button from '@/components/Button';
import TextField from '@/components/TextField';
import Loader from '@/components/Loader.tsx';
import { TypoCallout } from '@/components/Typography.tsx';
import notify from '@/utils/notifications.tsx';
import { getFieldError } from '@/utils/referral.ts';

// Centres a 44px button on a TextField's 56px input from below, so a wrapping label doesn't shift it. Rows keep
// any error text out of the field for the same reason.
const FIELD_BUTTON_CLASSES = 'shrink-0 mb-1.5';

interface ReferralInfo {
  referralCode: string;
  referralLink: string;
  sponsor: { clientId: number; ladderName: string } | null;
}

/** The viewer's own code and share link, and the player who referred them. Tracking only: no rewards. */
const ReferralSection = () => {
  const { data, loading } = useQuery(REFERRAL_INFO);
  const info: ReferralInfo | undefined = data?.user;

  if (loading && !info) return <Loader />;
  // Nothing useful to show without the referral fields (e.g. a backend that doesn't serve them yet).
  if (!info) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <TypoCallout className="text-neutral2-text-soft">
          Share your link. Anyone who signs up with it is linked to you as their sponsor.
        </TypoCallout>
        <CopyRow label="Your referral code" value={info.referralCode} />
        <CopyRow label="Your share link" value={info.referralLink} />
      </div>
      {info.sponsor ? (
        <TypoCallout className="text-neutral2-text-soft">
          Sponsored by <strong className="text-neutral2-text-strong">{info.sponsor.ladderName}</strong>
        </TypoCallout>
      ) : <SponsorForm />}
    </div>
  );
};

const CopyRow = ({ label, value }: { label: string; value: string }) => {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      notify('Copied!', `${label} is on your clipboard.`, { type: 'success' });
    } catch {
      notify('Couldn’t copy', 'Select the text and copy it instead.', { type: 'error' });
    }
  };

  return (
    <div className="flex items-end gap-3">
      <TextField label={label} value={value} readOnly className="flex-1 min-w-0" />
      <Button color="secondary" onClick={onCopy} className={FIELD_BUTTON_CLASSES}>Copy</Button>
    </div>
  );
};

const SponsorForm = () => {
  const [ladderName, setLadderName] = useState('');
  const [setSponsor, { loading, error }] = useMutation(SET_SPONSOR, {
    refetchQueries: [{ query: REFERRAL_INFO }],
    awaitRefetchQueries: true,
    onCompleted: () => notify('Success!', 'Your sponsor was saved.', { type: 'success' }),
    onError: (apolloError) => console.log(apolloError),
  });
  // Unknown name, a sponsor already attached, or the viewer's own name.
  const errorText = error && (getFieldError(error, 'ladder_name') ?? error.graphQLErrors[0]?.message);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (ladderName.trim()) setSponsor({ variables: { ladderName: ladderName.trim() } });
  };

  return (
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-3">
      <div className="flex items-end gap-3">
        <TextField
          type="text"
          label="Who referred you? (Ladder name)"
          placeholder="Their Ladder name"
          value={ladderName}
          onChange={event => setLadderName(event.target.value)}
          error={Boolean(errorText)}
          className="flex-1 min-w-0"
        />
        <Button type="submit" loading={loading} disabled={!ladderName.trim()} className={FIELD_BUTTON_CLASSES}>
          Save
        </Button>
      </div>
      {errorText && <TypoCallout className="pl-2 text-danger-neutral-text-softer">{errorText}</TypoCallout>}
      <TypoCallout className="text-neutral2-text-softer">
        This can only be set once. Contact us if it needs changing later.
      </TypoCallout>
    </form>
  );
};

export default ReferralSection;
